import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";

const usePageTimeTracker = ({
  apiUrl,
  syncInterval = 30000, // 30 sec
  idleTimeout = 300000, // 5 min
}) => {
  const location = useLocation();

  const user = sessionStorage.getItem("user");
  const token= JSON.parse(user)?.token

  const STORAGE_KEY = `page_tracking_${token}`;

  const currentPageRef = useRef(location.pathname);
  const startTimeRef = useRef(Date.now());

  const isIdleRef = useRef(false);
  const isHiddenRef = useRef(false);

  // -----------------------------
  // Get stored page times
  // -----------------------------
  const getStoredData = () => {
    try {
      return (
        JSON.parse(
          localStorage.getItem(STORAGE_KEY)
        ) || {}
      );
    } catch {
      return {};
    }
  };

  // -----------------------------
  // Save page time (+ logic)
  // -----------------------------
  const addPageTime = useCallback(
    (page, seconds) => {
      if (!seconds || seconds <= 0)
        return;

      const existing =
        getStoredData();

      existing[page] =
        (existing[page] || 0) +
        seconds;

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(existing)
      );
    },
    [STORAGE_KEY]
  );

  // -----------------------------
  // Calculate active seconds
  // -----------------------------
  const calculateTimeSpent =
    useCallback(() => {
      if (
        isIdleRef.current ||
        isHiddenRef.current
      ) {
        return 0;
      }

      const seconds = Math.floor(
        (Date.now() -
          startTimeRef.current) /
          1000
      );

      startTimeRef.current =
        Date.now();

      return seconds;
    }, []);

  // -----------------------------
  // API Sync
  // -----------------------------
  const syncToServer =
    useCallback(
      async (
        useBeacon = false
      ) => {
        const storedData =
          getStoredData();

        if (
          !Object.keys(storedData)
            .length
        )
          return;

        const payload = {
          token,
          pages: storedData,
          lastUpdated:
            new Date().toISOString(),
        };

        try {
          if (
            useBeacon &&
            navigator.sendBeacon
          ) {
            const blob =
              new Blob(
                [
                  JSON.stringify(
                    payload
                  ),
                ],
                {
                  type: "application/json",
                }
              );

            navigator.sendBeacon(
              apiUrl,
              blob
            );
          } else {
            const response =
              await fetch(apiUrl, {
                method: "POST",
                headers: {
                  "Content-Type":
                    "application/json",
                },
                body: JSON.stringify(
                  payload
                ),
              });

            if (!response.ok) {
              throw new Error(
                "API Failed"
              );
            }
          }
        } catch (error) {
          console.error(
            "Tracking API Error",
            error
          );
        }
      },
      [apiUrl, token]
    );

  // -----------------------------
  // Save current page time
  // -----------------------------
  const saveCurrentPageTime =
    useCallback(() => {
      const seconds =
        calculateTimeSpent();

      addPageTime(
        currentPageRef.current,
        seconds
      );
    }, [
      calculateTimeSpent,
      addPageTime,
    ]);

  // -----------------------------
  // Route change
  // -----------------------------
  useEffect(() => {
    if (
      currentPageRef.current !==
      location.pathname
    ) {
      saveCurrentPageTime();

      currentPageRef.current =
        location.pathname;

      startTimeRef.current =
        Date.now();
    }
  }, [
    location.pathname,
    saveCurrentPageTime,
  ]);

  // -----------------------------
  // Tab hidden/minimized
  // -----------------------------
  useEffect(() => {
    const handleVisibility =
      () => {
        if (
          document.hidden
        ) {
          saveCurrentPageTime();

          isHiddenRef.current =
            true;
        } else {
          isHiddenRef.current =
            false;

          startTimeRef.current =
            Date.now();
        }
      };

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      );
    };
  }, [saveCurrentPageTime]);

  // -----------------------------
  // Browser close / refresh
  // -----------------------------
  useEffect(() => {
    const handleUnload =
      () => {
        saveCurrentPageTime();

        syncToServer(true);
      };

    window.addEventListener(
      "beforeunload",
      handleUnload
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleUnload
      );
    };
  }, [
    saveCurrentPageTime,
    syncToServer,
  ]);

  // -----------------------------
  // Idle Timer
  // -----------------------------
  useIdleTimer({
    timeout: idleTimeout,

    onIdle: () => {
      saveCurrentPageTime();
      isIdleRef.current = true;
    },

    onActive: () => {
      isIdleRef.current = false;

      startTimeRef.current =
        Date.now();
    },

    debounce: 500,
  });

  // -----------------------------
  // Auto sync every 30 sec
  // -----------------------------
  useEffect(() => {
    const interval =
      setInterval(() => {
        saveCurrentPageTime();
        syncToServer();
      }, syncInterval);

    return () =>
      clearInterval(interval);
  }, [
    syncInterval,
    saveCurrentPageTime,
    syncToServer,
  ]);

  // -----------------------------
  // Component unmount
  // -----------------------------
  useEffect(() => {
    return () => {
      saveCurrentPageTime();
      syncToServer(true);
    };
  }, [
    saveCurrentPageTime,
    syncToServer,
  ]);

  // -----------------------------
  // Clear data on token change
  // -----------------------------
  useEffect(() => {
    return () => {
      localStorage.removeItem(
        STORAGE_KEY
      );
    };
  }, [STORAGE_KEY]);
};

export default usePageTimeTracker;